(ns io.daskul.webapp.widgets.display
  "Generic display renderers for computed/enriched field values.

   Renders field values based on :ui :display config from aggregate definitions.
   Supports :badge type (colored status badges) with extensible :type dispatch."
  (:require
   [clojure.string :as str]
   [uix.core :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.format :as format]
   [io.daskul.webapp.widgets.display-logic :as display-logic]
   [io.daskul.webapp.widgets.fsm.badge :as fsm.badge]
   [io.daskul.webapp.widgets.fsm.colors :as colors]))

(defn humanize-enum-value
  "Convert raw enum string to human-readable label.
   Splits on hyphens and underscores, capitalizes each word.
   \"session-based\" → \"Session Based\"
   \"early_childhood\" → \"Early Childhood\""
  [v]
  (when (string? v)
    (->> (str/split v #"[-_]")
         (map str/capitalize)
         (str/join " "))))

(defn humanize-field-name
  "Convert a keyword field name to a human-readable label.
   Splits on hyphens, capitalizes each word.
   :name → \"Name\"
   :session-duration → \"Session Duration\"
   :date-of-birth → \"Date Of Birth\""
  [field-kw]
  (->> (str/split (name field-kw) #"-")
       (map str/capitalize)
       (str/join " ")))

(defn resolve-path
  "Resolve a :path value from a record.

   Simple path:   [:program :name]
     → (get-in record [:program :name])

   Compound path: [[:student :person :first-name] \" \" [:student :person :last-name]]
     → concatenates each part; vectors are resolved via get-in,
       strings are used as literal separators, nil values are omitted.
       Returns nil when no vector parts resolve to a value (separators alone don't count)."
  [record path]
  (if (vector? (first path))
    (let [resolved (map (fn [part]
                          (if (string? part)
                            part
                            (get-in record part)))
                        path)
          ;; Check that at least one non-separator part has a value
          has-data? (some some? (map #(when (vector? %2) %1) resolved path))]
      (when has-data?
        (->> resolved (remove nil?) (str/join))))
    (get-in record path)))

(def ^:private uppercase-label
  "Status chips read as uppercase with a touch of letterspacing (#858) — the
   Linear/Stripe/GitHub status convention, and letterspacing keeps all-caps from
   looking cramped. A CSS transform, not a string upcase, so screen readers,
   copy-paste and e2e text assertions still see the original casing (\"Active\").
   Shared by the badge and the light indicator so both stay in step."
  {:textTransform "uppercase" :letterSpacing "0.05em"})

(defui value-badge
  "Renders a value as a colored badge using a value→config map.
   Supports icon (via :icon) and tooltip (via :description) per value.

   Props:
     value          - Current value string (e.g., \"draft\")
     display-config - {:type :badge
                       :values {\"draft\" {:label \"Draft\" :variant \"info\"
                                          :icon \"pencil\" :description \"In draft\"}}}"
  [{:keys [value display-config]}]
  (let [;; JSON round-trip keywordizes map keys, but data values are strings.
        ;; Look up both keyword and string to handle either format.
        value-config (or (get (:values display-config) (keyword value))
                         (get (:values display-config) value))
        label (or (:label value-config) value)
        icon-name (:icon value-config)
        description (:description value-config)
        ;; Colours come from the lifecycle-role palette rather than Mantine's
        ;; `light` variant, which rendered mid-tone text on a 10%-alpha wash of
        ;; the same hue — every state badge measured 2.9–3.2:1 at 11px, under
        ;; the 4.5:1 AA floor (#634). `colors-test` pins the floor.
        {:keys [bg fg]} (colors/badge-colors value-config)
        badge-el ($ ui/badge
                    {:size "md"
                     :style (merge {:backgroundColor bg :color fg} uppercase-label)
                     :leftSection (when icon-name (fsm.badge/get-icon icon-name 12))}
                    label)]
    (if description
      ($ ui/tooltip {:label description :withArrow true} badge-el)
      badge-el)))

(defui indicator
  "Renders an `:indicator` cell (#858/#870) — one marker primitive whose *weight*
   is chosen per column, its meaning fixed. Colour comes from the same `:tone`
   palette the badge uses, so green+Active is green+Active on every surface —
   consistent in meaning, the weight scales with context. A nil value renders the
   muted dash; a real 0 renders as \"0\".

   Two weights:
     - default: a light marker — `[icon] label`, both in the tone foreground, no
       fill. For ambient, low-information columns like Status where most rows
       read the same and a filled pill would be noise.
       {:type :indicator :icon \"dot\"} → ● Active
     - `:filled true`: the full `value-badge` pill (tone bg + fg + icon +
       tooltip). For an action signal that should pull the eye on the few rows
       that carry it — a count waiting to be worked.
       {:type :indicator :filled true :icon \"receipt\" :tone :attention
        :singular \"unbilled enrollment\"} → 🧾 2 pill, tooltip \"2 unbilled enrollments\"

   For an enum the per-row state-UI is injected as :values by render-column-cell,
   so both weights read the state's own colour + label."
  [{:keys [value display-config]}]
  (if-let [{:keys [label color-source description]} (display-logic/indicator value display-config)]
    (let [icon-name (:icon display-config)]
      (if (:filled display-config)
        ;; The pill treatment, delegated to value-badge so there is one pill in
        ;; the codebase — same contrast-tested colours, icon and tooltip wiring.
        ($ value-badge
           {:value (str value)
            :display-config {:type :badge
                             :values {(str value) (merge color-source
                                                         {:label label
                                                          :icon icon-name
                                                          :description description})}}})
        ;; The light marker — fg-only icon + coloured text, optional tooltip.
        (let [{:keys [fg]} (colors/badge-colors color-source)
              el ($ :span {:style {:display "inline-flex" :alignItems "center"
                                   :gap 4 :color fg :whiteSpace "nowrap"}}
                    (when icon-name (fsm.badge/get-icon icon-name 14 fg))
                    ($ :span {:style (merge {:fontSize "0.7rem"} uppercase-label
                                            {:fontWeight 600 :letterSpacing "0.08em"})} label))]
          (if description
            ($ ui/tooltip {:label description :withArrow true} el)
            el))))
    "-"))

(defui count-breakdown
  "Renders a per-state count map (#813) as one line — \"3 · 1 draft\".

   The primary count carries normal weight, secondaries are dimmed so the
   number stays the thing you scan, and a zero primary takes the warning
   token: that cell is the whole point of the column.

   Props:
     value          - {<state> n}, e.g. {:active 3 :draft 1}
     display-config - {:type :count-breakdown :primary :active
                       :secondary [:draft] :zero-label \"No active\"}"
  [{:keys [value display-config]}]
  ($ ui/text {:size "sm"}
     ;; One text run with literal spaces between the parts, not a flex row:
     ;; a `gap` leaves no whitespace in the DOM, so the cell would read back
     ;; as \"3· 1 draft\" to a screen reader, a copy-paste, or an e2e assertion.
     (interpose
      " "
      (for [{:keys [text tone]} (display-logic/count-breakdown-segments value display-config)]
        ($ :span (cond-> {:key text}
                   (= :secondary tone) (assoc :style {:color "var(--mantine-color-dimmed)"})
                   (= :warning tone) (assoc :style {:color "var(--warning-fg)"}))
           (if (= :warning tone) (str "⚠ " text) text))))))

(defn render-display-value
  "Render a value using its :display config. Returns a React element.

   display-config - {:type :badge :values {...}} or nil
   value          - The raw field value

   Falls back to (str value) if type is unknown."
  [value display-config]
  (if-let [t (some-> (:type display-config) keyword)]
    (case t
      :badge ($ value-badge {:value value :display-config display-config})
      :currency (if (and (:zero-dash display-config)
                         (display-logic/zero-amount? value))
                  "-"
                  (format/format-currency value))
      :indicator ($ indicator {:value value :display-config display-config})
      :count-breakdown ($ count-breakdown {:value value :display-config display-config})
      (str value))
    (str value)))

(defn state-ui->display-config
  "Convert per-item FSM state-ui to display-config format for value-badge.
   state    - Current state string (e.g., \"draft\")
   state-ui - UI metadata map with :label, :description, :icon, :variant

   This is the FOCAL treatment — the full badge. The detail record header uses
   it; dense list / child-table cells use `state-cell-display-config` (the
   lighter dot) instead."
  [state state-ui]
  {:type :badge :values {state state-ui}})

;; The dense-list / child-table Status default (#858) — the lighter dot. Kept
;; pure in display-logic (node-testable); re-exported here so the render paths
;; reach it through `display/…` alongside `state-ui->display-config`.
(def state-cell-display-config display-logic/state-cell-display-config)

(defn- state-field?
  "Returns true if a display-section field path ends with :state."
  [path]
  (and (vector? path)
       (seq path)
       (= :state (if (vector? (first path))
                   ;; Compound path — not a state field
                   nil
                   (last path)))))

(defn- auto-display-config
  "Auto-construct a display config for a :state field if possible.
   For own state ([:state]): uses _meta.fsm.state from the record.
   For related state ([:rel :state]): uses state-display-configs lookup.

   Returns a display-config map or nil."
  [path value record state-display-configs]
  (when (state-field? path)
    (if (= [:state] path)
      ;; Own aggregate state — use per-item FSM metadata
      (when-let [state-ui (get-in record [:_meta :fsm :state])]
        (state-ui->display-config value state-ui))
      ;; Related aggregate state — use state-display from /_config
      (let [rel-key (first path)]
        (get state-display-configs rel-key)))))

(defui display-section-item
  "Renders a single display section. Hidden when all fields resolve to nil.

   Props:
     record                - The full aggregate record
     section               - Section config with :title, :fields, and optional :link
     make-link-url         - Optional (fn [agg-key id] url-or-nil) for link resolution
     state-display-configs - Optional {rel-key display-config} for auto-detecting :state badge rendering"
  [{:keys [record section make-link-url state-display-configs]}]
  (let [fields (:fields section)
        resolved (mapv #(resolve-path record (:path %)) fields)
        has-values? (some some? resolved)
        link (:link section)
        link-id (when link (get-in record (:id-path link)))
        link-url (when (and make-link-url link link-id)
                   (make-link-url (:aggregate link) link-id))]
    (when has-values?
      ($ ui/paper {:p "md" :withBorder true :mb "md"}
         ($ ui/stack {:gap "xs"}
            ($ ui/group {:justify "space-between" :mb "xs"}
               ($ ui/text {:size "xs" :fw 700 :tt "uppercase"
                           :style {:letterSpacing "0.06em"}} (:title section))
               (when link-url
                 ($ ui/action-icon {:component "a" :href link-url
                                    :variant "subtle" :size "sm" :color "dimmed"}
                    ($ icons/icon-external-link {:size 16}))))
            (for [field (map vector fields resolved)]
              (let [[f value] field
                    display-config (or (:display f)
                                       (auto-display-config (:path f) value record state-display-configs))]
                (when (some? value)
                  ($ ui/group {:key (:label f) :gap "sm" :wrap "nowrap"}
                     ($ ui/text {:size "sm" :c "dimmed"
                                 :style {:minWidth "130px" :flexShrink 0}} (:label f))
                     (if display-config
                       (render-display-value value display-config)
                       ($ ui/text {:size "sm"} (str value))))))))))))

(defui display-sections
  "Renders read-only context sections above the edit form in the detail view.

   Each section has a :title and a list of :fields, each with a :label and :path.
   Sections with optional :link config show a navigation icon in the title row.
   Sections where ALL fields resolve to nil are hidden entirely.

   Props:
     record                - The full aggregate record (with includes resolved)
     sections              - Vector of section configs from :ui :detail :display-sections
     make-link-url         - Optional (fn [agg-key id] url-or-nil) for link resolution
     state-display-configs - Optional {rel-key display-config} for auto-detecting :state badge rendering"
  [{:keys [record sections make-link-url state-display-configs]}]
  (when (seq sections)
    ($ :<>
       (for [section sections]
         ($ display-section-item {:key (:title section)
                                  :record record
                                  :section section
                                  :make-link-url make-link-url
                                  :state-display-configs state-display-configs})))))
